<template>
  <div class="container">
    <div v-if="latestPowerLogOffline" class="alert alert-warning d-flex align-items-center mb-4">
      <i class="far fa-fw fa-wifi-slash me-2"></i>
      <span>
        Device offline.
      </span>
    </div>
    <div class="card card-xs-full" v-if="!this.viewAll">
      <div class="card-body pb-0">
        <div class="hstack mb-5">
          <h5 class="mb-0">Power Consumption - Amps</h5>
          <div class="ms-auto">
            <power-consumption-list-filters v-model="params" />
          </div>
        </div>
        <power-consumption-amps-list :graphOptions="graphOptions('Amps')" :series="ampsSeries" :chartKey="chartKey" />
      </div>
    </div>
    <power-consumption-kwh-list :graphOptions="graphOptions('Kw/h (Average)')" title="Average Power Consumption – kWh" :series="kwhSeries" v-if="!this.viewAll" :chartKey="chartKey" />
    <div v-if="!this.viewAll">
      <power-consumption-kwh-list title="Total Power Consumption – kWh" :graphOptions="graphOptions('kWh (Total)')" :series="totalKwhSeries" @downloadTotalsCsv="downloadTotalsCsv" :chartKey="chartKey" />
    </div>
    <power-consumption-logs-list :viewAll="viewAll" @setAll="setAll" />
  </div>
</template>

<script>
import dayjs from 'dayjs'
import swal from 'sweetalert'
import { http } from '@/utils'
import { helperComputed, helperMethods } from '@/vuex/modules/screens/consumption-logs'
import { compareQueryParams } from '@codium/codium-vue'
import { get } from 'vuex-pathify'
import PowerConsumptionAmpsList from './PowerConsumptionAmpsList.vue'
import PowerConsumptionKwhList from './PowerConsumptionKwhList.vue'
import PowerConsumptionListFilters from './PowerConsumptionListFilters.vue'
import PowerConsumptionLogsList from './PowerConsumptionLogsList.vue'

export default {
  components: {
    PowerConsumptionAmpsList,
    PowerConsumptionKwhList,
    PowerConsumptionListFilters,
    PowerConsumptionLogsList
  },

  data () {
    return {
      params: {
        startDate: dayjs().format('YYYY-MM-DD'),
        endDate: dayjs().add(6, 'day').format('YYYY-MM-DD'),
        granularity: 'day'
      },
      viewAll: false,
      latestPowerLogOffline: false
    }
  },
  computed: {
    ...helperComputed,
    screen: get('screens/screen'),
    kwhSeries () {
      const logs = this.getLogsForChart()
      if (logs.length) {
        return [{
          data: logs.map((item) => {
            return {
              x: item.period,
              y: item.average_power_consumption_kwh ?? 0
            }
          })
        }]
      }

      return []
    },
    ampsSeries () {
      const logs = this.getLogsForChart()
      if (logs.length) {
        return [{
          data: logs.map((item) => {
            return {
              x: item.period,
              y: item.average_power_consumption_amps ?? 0
            }
          })
        }]
      }

      return []
    },
    totalKwhSeries () {
      const logs = this.getLogsForChart()
      if (logs.length) {
        return [{
          data: logs.map((item) => {
            return {
              x: item.period,
              y: item.total_power_consumption_kwh ?? 0
            }
          })
        }]
      }
      return []
    },
    tooManyDailyDays () {
      const { startDate, endDate } = this.params || {}
      if (!startDate || !endDate) {
        return 0
      }
      const rangeDays = dayjs(endDate).diff(dayjs(startDate), 'day') + 1
      return this.params?.granularity === 'day' && rangeDays > 92
    },
    labels () {
      const logs = this.getLogsForChart()
      if (!logs.length) {
        return []
      }
      return logs.map(log => log.period)
    },
    chartKey () {
      const { granularity, startDate, endDate } = this.params || {}
      return `${granularity || ''}-${startDate || ''}-${endDate || ''}`
    }
  },

  watch: {
    params: compareQueryParams(async function (params) {
      if (this.params.endDate && this.params.startDate) {
        try {
          if (this.tooManyDailyDays) {
            await swal({
              title: 'Invalid date range.',
              text: 'Days must be less than 92 total.',
              buttons: {
                confirm: { text: 'Close' }
              }
            })
            return
          }
          this.resetConsumptionLogs()
          await this.getConsumptionLogs({ params, fresh: true })
        } catch (err) {
          await swal({
            title: 'Invalid date range.',
            text: err.response.data.message,
            buttons: {
              confirm: { text: 'Close' }
            }
          })
          throw err
        }
      }
    })
  },

  beforeDestroy () {
    this.resetConsumptionLogs()
  },
  async created () {
    await this.loadLatestPowerLog()

    if (this.params.endDate && this.params.startDate) {
      await this.getConsumptionLogs({ params: this.params })
    }
  },

  methods: {
    ...helperMethods,

    async loadLatestPowerLog () {
      try {
        const response = await http.get(`screens/${this.$route.params.screenId}/logs`)
        this.latestPowerLogOffline = response?.data?.data?.[0]?.is_offline ?? false
      } catch {
        this.latestPowerLogOffline = false
      }
    },

    getLogsForChart () {
      if (!this.consumptionLogs || !Array.isArray(this.consumptionLogs)) {
        return []
      }

      const logs = this.consumptionLogs.slice()

      if (this.params && this.params.granularity === 'hour') {
        const now = dayjs()
        return logs.filter(item => {
          const timestamps = dayjs(item.period)
          if (!timestamps.isValid()) {
            return true
          }
          return !timestamps.isAfter(now)
        })
      }

      return logs
    },

    downloadTotalsCsv () {
      if (!this.consumptionLogs || !this.consumptionLogs.length) {
        return
      }

      const escapeCsv = (value) => {
        if (value === undefined || value === null) {
          return ''
        }
        const str = String(value)
        const needsQuoting = /[",\n\r]/.test(str)
        const escaped = str.replace(/"/g, '""')
        return needsQuoting ? '"' + escaped + '"' : escaped
      }

      const header = ['Period', 'Total kWh']
      const rows = this.consumptionLogs.map(item => [item.period, item.total_power_consumption_kwh ?? 0])
      const csvContent = [header, ...rows]
        .map(r => r.map(escapeCsv).join(','))
        .join('\n')

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.setAttribute('href', url)

      const parts = ['TotalPowerConsumption']

      if (this.screen && this.screen.name) {
        const sanitized = this.screen.name.replace(/\s+/g, '-').replace(/[^a-zA-Z0-9_-]/g, '')
        parts.push(sanitized)
      }

      if (this.params.startDate && this.params.endDate) {
        const startDate = dayjs(this.params.startDate).format('YYYYMMDD')
        const endDate = dayjs(this.params.endDate).format('YYYYMMDD')
        parts.push(`${startDate}-${endDate}`)
      }

      const filename = `${parts.join('-')}.csv`
      link.setAttribute('download', filename)
      link.style.display = 'none'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    },

    async load (done) {
      const { params } = this

      try {
        if (this.params.endDate && this.params.startDate) {
          await this.getConsumptionLogs({ params })
        }

        done()
      } catch {
        done(true)
      }
    },
    setAll (value) {
      this.viewAll = value
    },

    graphOptions (label) {
      const shouldRotateWeekly = this.params && this.params.granularity === 'custom' && Array.isArray(this.labels) && this.labels.length > 7
      const shouldRotateDaily = this.params && this.params.granularity === 'day' && Array.isArray(this.labels) && this.labels.length > 15
      const shouldHideDaily = this.params && this.params.granularity === 'day' && Array.isArray(this.labels) && this.labels.length > 30
      const rotate = shouldRotateWeekly ? -45 : shouldRotateDaily ? -45 : 0
      const rotateAlways = shouldRotateWeekly || shouldRotateDaily
      const offsetX = shouldRotateWeekly ? 8 : shouldRotateDaily ? 8 : 0
      const offsetY = shouldRotateWeekly ? 4 : shouldRotateDaily ? 4 : 0
      return {
        chart: {
          height: 350,
          type: 'bar',
          toolbar: {
            show: false // This will remove the download/export options
          },
        },
        states: {
          hover: {
            filter: {
              type: 'darken',
              value: 0.15,
            }
          },
        },
        plotOptions: {
          bar: {
            horizontal: false,
            columnWidth: '55%',
            endingShape: 'rounded'
          },
        },
        dataLabels: {
          enabled: false,
          position: 'top'
        },
        stroke: {
          show: false
        },
        xaxis: {
          categories: this.labels,
          labels: {
            show: !shouldHideDaily,
            trim: false,
            rotate: rotate,
            rotateAlways: rotateAlways,
            offsetX: offsetX,
            offsetY: offsetY,
            hideOverlappingLabels: false,
            showDuplicates: true,
          }
        },
        yaxis: {
          show: false,
        },
        fill: {
          type: ['solid'],
          opacity: 1,
          colors: ['#dee2e6', '#464646']
        },
        onDatasetHover: {
          highlightDataSeries: false,
        },
        tooltip: {
          enabled: true,
          style: {
            fontSize: '14px',
            backgroundColor: '#464646 ',
            color: '#fff',
          },
          x: {
            show: shouldHideDaily,
          },
          y: {
            formatter: function (val) {
              return `${val} ${label}`
            },
            title: {
              formatter: () => '',
            },
          },
          marker: {
            show: false,
          },
          fixed: {
            enabled: false,
          },
          theme: 'dark',
          fillSeriesColor: false,
        }
      }
    }
  }
}
</script>
